if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.isSubmitting = false;
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.isSubmitting || this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.isSubmitting = true;
        this.lnAnalyticsOutcomeTracked = false;
        const submitButtons = this.getSubmitButtons();

        this.handleErrorMessage();

        submitButtons.forEach((button) => {
          button.setAttribute('aria-disabled', true);
          button.classList.add('loading');
        });
        this.querySelector('.loading__spinner')?.classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              this.trackAnalyticsOutcome('error', formData, evt, response.description || response.message);
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) {
                // Compact LashNova PDPs keep a recoverable inline error rather
                // than permanently disabling the custom purchase button.
                this.error = false;
                return;
              }
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              this.trackAnalyticsOutcome('success', formData, evt);
              window.location = window.routes.cart_url;
              return;
            }

            this.trackAnalyticsOutcome('success', formData, evt);

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error) {
              // A secondary cart-update subscriber must never prevent the
              // authoritative add response from repainting the cart drawer.
              // Wrap both synchronous subscriber errors and rejected promises.
              let cartUpdatePublication;
              try {
                cartUpdatePublication = publish(PUB_SUB_EVENTS.cartUpdate, {
                  source: 'product-form',
                  productVariantId: formData.get('id'),
                  cartData: response,
                });
              } catch (subscriberError) {
                console.error('Cart update subscriber failed:', subscriberError);
              }

              Promise.resolve(cartUpdatePublication)
                .then(() => {
                  CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
                })
                .catch((subscriberError) => {
                  console.error('Cart update subscriber failed:', subscriberError);
                });
            }
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              try {
                CartPerformance.measure("add:paint-updated-sections", () => {
                  this.cart.renderContents(response);
                });
              } catch (renderError) {
                console.error('Cart drawer rendering failed:', renderError);
                window.location.assign(window.routes.cart_url);
              }
            }
          })
          .catch((e) => {
            console.error(e);
            this.trackAnalyticsOutcome('error', formData, evt, e);
            this.handleErrorMessage(window.cartStrings?.error || 'The cart could not be updated. Please try again.');
          })
          .finally(() => {
            this.isSubmitting = false;
            submitButtons.forEach((button) => {
              button.classList.remove('loading');
              if (!this.error) button.removeAttribute('aria-disabled');
            });
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            this.querySelector('.loading__spinner')?.classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      trackAnalyticsOutcome(outcome, formData, evt, error) {
        if (this.lnAnalyticsOutcomeTracked || !this.dataset.lnProductKind) return;
        this.lnAnalyticsOutcomeTracked = true;

        const analytics = window.LashNovaAnalytics;
        if (!analytics?.track) return;

        const kind = this.dataset.lnProductKind === 'bundle' ? 'bundle' : 'product';
        const quantity = Number(formData.get('quantity')) || 1;
        const price = Number(this.dataset.lnPrice) || 0;
        const sticky = evt?.submitter?.closest?.('[data-lnp-mobile-cart]');
        const surface = sticky
          ? `${kind === 'bundle' ? 'bundle' : 'single'}_pdp_sticky`
          : this.dataset.lnSurface || `${kind === 'bundle' ? 'bundle' : 'single'}_pdp_main`;

        try {
          analytics.track(`ln_${kind}_add_${outcome}`, {
            surface,
            product_id: Number(this.dataset.lnProductId) || undefined,
            product_handle: this.dataset.lnProductHandle,
            product_title: this.dataset.lnProductTitle,
            variant_id: Number(formData.get('id')) || undefined,
            quantity,
            value: price ? (price * quantity) / 100 : undefined,
            currency: this.dataset.lnCurrency,
            error_code: outcome === 'error' ? analytics.classifyError?.(error) : undefined,
          });
        } catch (_error) {
          // Analytics must never interrupt the authoritative cart response.
        }
      }

      getSubmitButtons() {
        const associatedSubmitButtons = Array.from(this.form.elements).filter(
          (element) => element.matches?.('button[type="submit"], input[type="submit"]')
        );

        return associatedSubmitButtons.length ? associatedSubmitButtons : [this.submitButton];
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector('.product-form__error-message-wrapper') ||
          this.closest('[data-lnp-root]')?.querySelector('[data-lnp-main-error]');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
